import { IQueryExpr, Query, UserDefinedNotificationConfig } from '@apis/Notification/model';
import { QueryConstant, QueryExpr, QueryOperation } from '@apis/Resources';
import { UserConfiguredBarChartSettings } from '@root/Components/Charts/BarChart';
import { ChartConfig } from '@root/Components/DashboardLayout/Charts/ChartRenderer';
import { createAccessor } from '@root/Components/Settings/InputSpec';
import { FormElement, SettingsForm } from '@root/Components/Settings/SettingsForms';
import { EventEmitter } from '@root/Services/EventEmitter';
import { InvoiceApiService } from '@root/Services/Invoices/InvoiceApiService';
import { IInvoiceFieldCompatibilityLookup, InvoiceFieldCompatibilityService } from '@root/Services/Invoices/InvoiceFieldCompatibilityService';
import { InvoiceQueryDatasourceFactory } from '@root/Services/Invoices/InvoiceQueryDatasourceFactory';
import { IBaseInvoiceRecord, IMixedInvoiceRecord, InvoiceSchemaService } from '@root/Services/Invoices/InvoiceSchemaService';
import { ExprEvaluator } from '@root/Services/Query/ArrayDataSource';
import { QueryDatasource } from '@root/Services/Query/QueryDatasource';
import { exprBuilder, SchemaService } from '@root/Services/QueryExpr';
import { ArrowsUpDown, CalendarStats, ChartBar, Coin, Percentage, Settings } from 'tabler-icons-react';
import { inject, injectable } from 'tsyringe';
import { describeDatasource } from '../../Components/DatasourceDescriptor';
import { RecurrenceRuleType } from '../../Components/Schedule';
import { BaseDatasourcePresetsBuilder, IDatasourcePresetsBuilderFactory, INotificationPresetItem, PrefixedParams } from './BasePresets';

class InvoiceDatasourcePresetsBuilder extends BaseDatasourcePresetsBuilder {
    public constructor(
        queryDatasource: QueryDatasource,
        schemaSvc: SchemaService,
        private readonly invoiceApi: InvoiceApiService,
        private readonly exprEvaluator: ExprEvaluator,
        private readonly fieldCompat: IInvoiceFieldCompatibilityLookup
    ) {
        super(queryDatasource, schemaSvc);
    }

    public getPresets() {
        const { name, icon: Icon } = describeDatasource(this.baseQueryDatasource);

        return this.createDatasourcePresets(
            name,
            <Icon />,
            [
                this.createAvgUsageChangePreset({ days: 10, percent: 0.2, change: 'increase' }),
                this.createAvgUsageChangePreset({ days: 10, percent: 0.2, change: 'decrease' }),
                this.createAvgUsageChangePreset({ days: 30, percent: 0.2, change: 'increase' }),
                this.createAvgUsageChangePreset({}),
                this.createCustomInvoiceAlertPreset(),
            ],
            [
                this.createSchedUsageCostPreset({ days: 15, period: 'Weekly' }),
                this.createSchedUsageCostPreset({ days: 30, period: 'Weekly' }),
                this.createSchedTotalCostPreset({ days: 15, period: 'Weekly' }),
                this.createSchedTotalCostPreset({ days: 30, period: 'Weekly' }),
                this.createSchedMonthToDateCostPreset('usage', { recurrence: 'Weekly' }),
                this.createSchedMonthToDateCostPreset('total', { recurrence: 'Weekly' }),
                this.createCustomCostReportPreset(),
            ]
        );
    }

    // #region Alerts
    private createAvgUsageChangePreset(options: {
        days?: number;
        percent?: number;
        change?: 'increase' | 'decrease' | 'change';
    }): INotificationPresetItem<any> {
        const { days, percent, change } = options;
        const { days: daysDef = days ?? 10, change: changeDef = change ?? 'increase', percent: percentDef = percent ?? 0.2 } = options;

        const daysDesc = 'days' in options ? `${options.days}-day ` : 'configured days of';
        const percentLbl = ` ${percentDef * 100}%`;
        const percentDesc = percent === undefined ? 'by a configured percent' : `by ${percentLbl}`;
        const changeDirLbl = options.change === 'increase' ? 'up' : options.change === 'decrease' ? 'down' : 'change';
        const changeDirDesc = options.change === 'increase' ? 'increases' : options.change === 'decrease' ? 'decreases' : 'changes';

        const id = this.createId(days, 'day-usage', change, 'by', percent);
        const description = `Alert when the ${daysDesc} daily usage cost ${changeDirDesc} ${percentDesc}`;

        type Params = PrefixedParams<Required<typeof options>>;

        const [dateExpr, costExpr] = this.getExprs<[Date, number]>(this.getFields('ChargePeriodStart', 'BilledCost'));
        const filterInput = this.createFilterSettings();

        const behavior = this.createBehavior(({ data }) => {
            const xb = this.createExprBuilder<Params>();
            const groups = this.selectExprs({ Date: dateExpr });
            const values = this.selectExprs({ 'Usage Cost': xb.sum(costExpr) });
            const defaultDays = Math.round(daysDef / 4);

            const result = this.mergeConfig(
                {
                    Title: 'Significant Usage Cost Change',
                    Description: '',
                    ComponentConfig: this.createBaseBarChartConfig(groups, values, {
                        xFormat: 'short-date',
                        format: 'money-with-two-decimals',
                        ...((data.ComponentConfig?.settings ?? {}) as UserConfiguredBarChartSettings),
                        metricLabel: 'Usage Cost',
                        reaggOptions: { sortBy: 'label', sortDir: 'asc' },
                    }),
                    // #region Threshold settings
                    Trigger: {
                        TriggerType: 'Threshold',
                        Threshold: {
                            ThrottleFrequency: 1,
                            ThrottlePeriod: 'Daily',
                            ThresholdQuery: data.Trigger?.Threshold?.ThresholdQuery ?? {
                                Select: [
                                    {
                                        Alias: 'selected',
                                        Expr: {
                                            Field: 'pSelected',
                                        },
                                    },
                                    {
                                        Alias: 'others',
                                        Expr: {
                                            Field: 'pOthers',
                                        },
                                    },
                                    {
                                        Alias: 'total',
                                        Expr: {
                                            Field: 'pLeftOp',
                                        },
                                    },
                                ],
                                Parameters: {
                                    pSource: {
                                        Operation: 'query',
                                        Operands: [
                                            {
                                                Field: '__pResults',
                                            },
                                            {
                                                __cast: 'query',
                                                Value: {
                                                    Sort: [
                                                        {
                                                            Direction: 'Desc',
                                                            Expr: {
                                                                Field: 'Date',
                                                            },
                                                        },
                                                    ],
                                                },
                                            },
                                        ],
                                    },
                                    pSelectedSet: {
                                        Operation: 'query',
                                        Operands: [
                                            {
                                                Field: 'pSource',
                                            },
                                            {
                                                __cast: 'query',
                                                Value: {
                                                    Take: defaultDays,
                                                },
                                            },
                                        ],
                                    },
                                    pSelected: {
                                        Operation: 'query',
                                        Operands: [
                                            {
                                                Field: 'pSelectedSet',
                                            },
                                            {
                                                __cast: 'query',
                                                Value: {
                                                    Select: [
                                                        {
                                                            Alias: 'total',
                                                            Expr: {
                                                                Operation: 'avg',
                                                                Operands: [
                                                                    {
                                                                        Field: 'Usage Cost',
                                                                    },
                                                                ],
                                                            },
                                                        },
                                                    ],
                                                },
                                            },
                                            {
                                                Value: 'total',
                                            },
                                            {
                                                Value: 'single',
                                            },
                                        ],
                                    },
                                    pOthersSet: {
                                        Operation: 'query',
                                        Operands: [
                                            {
                                                Field: 'pSource',
                                            },
                                            {
                                                __cast: 'query',
                                                Value: {
                                                    Skip: defaultDays,
                                                },
                                            },
                                        ],
                                    },
                                    pOthers: {
                                        Operation: 'query',
                                        Operands: [
                                            {
                                                Field: 'pOthersSet',
                                            },
                                            {
                                                __cast: 'query',
                                                Value: {
                                                    Select: [
                                                        {
                                                            Alias: 'selected',
                                                            Expr: {
                                                                Field: 'pSelected',
                                                            },
                                                        },
                                                        {
                                                            Alias: 'total',
                                                            Expr: {
                                                                Operation: 'avg',
                                                                Operands: [
                                                                    {
                                                                        Field: 'Usage Cost',
                                                                    },
                                                                ],
                                                            },
                                                        },
                                                    ],
                                                },
                                            },
                                            {
                                                Value: 'total',
                                            },
                                            {
                                                Value: 'single',
                                            },
                                        ],
                                    },
                                    pLeftOp: {
                                        Operation: 'query',
                                        Operands: [
                                            {
                                                __cast: 'records',
                                                Value: [{}],
                                            },
                                            {
                                                __cast: 'query',
                                                Value: {
                                                    Select: [
                                                        {
                                                            Alias: 'total',
                                                            Expr: {
                                                                Operation: 'divide',
                                                                Operands: [
                                                                    {
                                                                        Field: 'pSelected',
                                                                    },
                                                                    {
                                                                        Operation: 'add',
                                                                        Operands: [
                                                                            {
                                                                                Field: 'pSelected',
                                                                            },
                                                                            {
                                                                                Field: 'pOthers',
                                                                            },
                                                                        ],
                                                                    },
                                                                ],
                                                            },
                                                        },
                                                    ],
                                                },
                                            },
                                            {
                                                Value: 'total',
                                            },
                                            {
                                                Value: 'single',
                                            },
                                        ],
                                    },
                                },
                            },
                            ThresholdCondition: data.Trigger?.Threshold?.ThresholdCondition ?? {
                                Operation: changeDef === 'increase' ? 'gte' : 'lte',
                                Operands: [
                                    {
                                        Field: 'pLeftOp',
                                    },
                                    {
                                        Value: percentDef,
                                    },
                                ],
                            },
                            PresentationOptions: {
                                settings: [
                                    {
                                        description: 'Alert if usage cost changes significantly',
                                    },
                                ],
                            },
                        },
                    },
                    // #endregion
                    QueryOptions: {
                        Parameters: {
                            ...this.createUserInputParams(data, { pDays: daysDef, pPercent: percentDef, pChange: changeDef }),
                            pFrom: xb.resolve(xb.snapDate(xb.addDate(xb.currentDate(), xb.model.pDays.plus(1).times(-1), 'day'), 'day', 'start')),
                            pTo: xb.resolve(xb.snapDate(xb.addDate(xb.currentDate(), xb.param(-1), 'day'), 'day', 'end')),
                        },
                        Filters: [this.createUsageFilterExpr(), this.createDateFilter()],
                        DatasourceName: this.baseQueryDatasource.name,
                        ApiParameters: {
                            from: xb.resolve(xb.model.pFrom),
                            to: xb.resolve(xb.model.pTo),
                        },
                    },
                    PresentationOptions: { presetId: id },
                },
                data
            );

            return result;
        })
            .addForm(({ data }) => {
                const params = data.QueryOptions!.Parameters!;
                type Params = typeof params;

                const elements = [] as FormElement[];
                const thresholdParams = data.Trigger!.Threshold!.ThresholdQuery!.Parameters as {
                    pSelectedSet: QueryOperation;
                    pOthersSet: QueryOperation;
                };
                const accTakeDays = createAccessor((t: typeof thresholdParams) => t.pSelectedSet.Operands![1].Value.Take as number);
                const accSkipDays = createAccessor((t: typeof thresholdParams) => t.pOthersSet.Operands![1].Value.Skip as number);
                const accCond = createAccessor((d: typeof data) => d.Trigger!.Threshold!.ThresholdCondition);
                const xb = this.createExprBuilder<{ pLeftOp: number }>();
                const updateQuery = () => {
                    const days = Math.round((params.pDays?.Value ?? daysDef) / 4);
                    accTakeDays.set(thresholdParams, days);
                    accSkipDays.set(thresholdParams, days);

                    const percent = params.pPercent?.Value ?? percentDef;
                    const change = params.pChange.Value ?? changeDef;
                    const condition =
                        change === 'change'
                            ? xb.or(xb.model.pLeftOp.gte(percent), xb.model.pLeftOp.lte(percent))
                            : change === 'increase'
                            ? xb.model.pLeftOp.gte(percent)
                            : change === 'decrease'
                            ? xb.model.pLeftOp.lte(percent)
                            : null;
                    if (condition) {
                        accCond.set(data, xb.resolve(condition));
                    }
                };

                if (days === undefined) {
                    elements.push(
                        this.createInput<Params>()
                            .type('number')
                            .options({ label: 'Days', min: 7, max: 45, icon: <ChartBar /> })
                            .map((d) => d.pDays.Value)
                            .bind(params, updateQuery)
                    );
                }
                if (percent === undefined) {
                    elements.push(
                        this.createInput<Params>()
                            .type('number')
                            .options({ label: 'Percent', icon: <Percentage /> })
                            .map((d) => d.pPercent.Value)
                            .modelToView((v) => v * 100)
                            .viewToModel((v) => v / 100)
                            .bind(params, updateQuery)
                    );
                }
                if (change === undefined) {
                    elements.push(
                        this.createInput<Params>()
                            .type('string')
                            .options({
                                label: 'Change Type',
                                options: [
                                    { value: 'increase', label: 'Increase' },
                                    { value: 'decrease', label: 'Decrease' },
                                    { value: 'change', label: 'Increase or Decrease' },
                                ],
                                icon: <ArrowsUpDown />,
                            })
                            .presentation({ width: 150, align: 'center' })
                            .map((d) => d.pChange.Value)
                            .bind(params, updateQuery)
                    );
                }

                const chart = data.ComponentConfig as unknown as ChartConfig;
                const costFld = this.createCostFieldOptions<typeof chart>()
                    ?.map((d) => d.values[0].Expr.Operands[0].Field!)
                    .modelToView((v) => v)
                    .viewToModel((v) => v)
                    .bind(chart);

                if (costFld) {
                    elements.push(costFld);
                }

                const filterSetting = filterInput(data, updateQuery);

                const settingForm = this.createAlertSettingsForm('Usage cost alert settings', ...this.createDescriptionInputs(data));
                settingForm.elements.push(this.createSectionItem(`Data Options`, ...this.concatSectionItems(elements, [filterSetting])));
                return [settingForm];
            })
            .addValidation(() => []);

        return {
            id,
            icon: !days || !percent || !change ? <Settings /> : <ChartBar />,
            label: [
                days === undefined ? { type: 'variable', text: '##' } : days.toString(),
                '-day daily usage ',
                change === undefined ? { type: 'variable', text: 'up/down' } : changeDirLbl,
                percent === undefined ? { type: 'variable', text: '##%' } : percentLbl,
            ],
            description,
            behavior,
        };
    }

    private createAlertSettingsForm(header: string, ...elements: FormElement[]) {
        return this.createSection('Alert Settings', {
            header: { label: header },
            elements,
        });
    }

    private createCustomInvoiceAlertPreset(): INotificationPresetItem<any> {
        const dimExpr = this.baseQueryDatasource.getDefaultGroup();
        const metricExpr = this.baseQueryDatasource.getDefaultValue();

        const filterInput = this.createFilterSettings();
        const chartInput = this.createCustomChartBehavior(true);
        const monthLookbackInput = this.createMonthLookbackInput();

        const behavior = this.createBehavior(({ data }) => {
            return this.mergeConfig(
                {
                    ComponentConfig: this.createCustomChartConfig('bar', dimExpr, metricExpr),
                    QueryOptions: {
                        ...this.defaultQueryOptions(this.baseQueryDatasource.name),
                        Parameters: {
                            ...this.createMonthLookbackParams(),
                            ...this.createUserInputParams(data, { pMonths: 1 }),
                        },
                        ApiParameters: this.createApiParams(),
                    },
                    Trigger: this.defaultAlertTrigger(),
                },
                data
            );
        })
            .addForm(({ data }) => {
                const configChanged = new EventEmitter(data.ComponentConfig!);
                const chartSettings = chartInput(data, configChanged.emit);

                const monthsSetting = monthLookbackInput(data, () => configChanged.emit(data.ComponentConfig!));
                const filterSetting = filterInput(data, configChanged.emit);
                const filterSection = this.createSectionItem('Invoice Line Item Filters', filterSetting, { type: 'divider' }, monthsSetting);

                chartSettings.elements.push(filterSection);

                return [
                    chartSettings,
                    this.createSection(
                        'Alert Settings',
                        this.createSectionItem('Title and Description', ...this.createDescriptionInputs(data)),
                        this.createAlertConditionSection(data, configChanged)
                    ),
                ];
            })
            .addValidation(() => []);

        return this.createCustomAlertPreset(behavior);
    }
    // #endregion

    // #region Reports
    private createSchedUsageCostPreset(options: { days?: number; period?: RecurrenceRuleType }): INotificationPresetItem<any> {
        return this.createSchedCostPreset('usage', options);
    }
    private createSchedTotalCostPreset(options: { days?: number; period?: RecurrenceRuleType }): INotificationPresetItem<any> {
        return this.createSchedCostPreset('total', options);
    }

    private createSchedCostPreset(
        costType: 'usage' | 'total' | 'user-defined',
        options: { days?: number; period?: RecurrenceRuleType }
    ): INotificationPresetItem<any> {
        const { days, period } = options;
        const { days: daysDef = days ?? 15, period: periodDef = period ?? 'Weekly' } = options;
        const costDesc = costType === 'usage' ? 'Usage Cost' : costType === 'total' ? 'Total Cost' : 'Cost';

        const id = this.createId(days, `${costType}-cost`, period);
        const description = this.createPresetDescription({ value: period, placeholder: 'Periodic' }, `report of usage cost over the previous`, {
            value: days,
            placeholder: 'configurable number of',
            suffix: ' days',
        });

        type Params = PrefixedParams<Required<typeof options>>;

        const [dateExpr, costExpr] = this.getExprs<[Date, number]>(this.getFields('ChargePeriodStart', 'BilledCost'));
        const filters = costType === 'total' ? [] : [this.createUsageFilterExpr()];
        filters.push(this.createDateFilter());

        const initialized = this.createBehavior(({ data }) => {
            const xb = this.createExprBuilder<Params>();
            const groups = this.selectExprs({ Date: dateExpr });
            const values = this.selectExprs({ Amount: xb.sum(costExpr) });

            return this.initializeConfig(data, id, {
                Title: `${costDesc} Report`,
                Description: '',
                ComponentConfig: this.createBaseBarChartConfig(groups, values, {
                    xFormat: 'short-date',
                    format: 'money-whole',
                    ...((data.ComponentConfig?.settings ?? {}) as UserConfiguredBarChartSettings),
                    metricLabel: 'Amount',
                    reaggOptions: { sortBy: 'label', sortDir: 'asc' },
                }),
                Trigger: this.defaultSchedTrigger(periodDef),
                QueryOptions: {
                    Parameters: {
                        ...this.createUserInputParams(data, { pDays: daysDef }),
                        pFrom: xb.resolve(xb.snapDate(xb.addDate(xb.currentDate(), xb.model.pDays.plus(1).times(-1), 'day'), 'day', 'start')),
                        pTo: xb.resolve(xb.snapDate(xb.addDate(xb.currentDate(), xb.param(-1), 'day'), 'day', 'end')),
                    },
                    Filters: filters,
                    DatasourceName: this.baseQueryDatasource.name,
                    ApiParameters: this.createApiParams(),
                },
            });
        });
        const behavior = initialized
            .addForm(({ data }) => {
                const params = data.QueryOptions!.Parameters;
                type Params = typeof params;

                const elements = [] as FormElement[];

                if (days === undefined) {
                    elements.push(
                        this.createInput<Params>()
                            .type('number')
                            .options({ label: 'Days', min: 7, max: 45 })
                            .map((d) => d!.pDays.Value)
                            .bind(params)
                    );
                }

                return [
                    this.createSection(
                        `Report Settings`,
                        this.createSectionItem(
                            `${costDesc} Report Options`,
                            ...this.concatSectionItems(elements, this.createDescriptionInputs(data))
                        ),
                        this.createScheduleForm(data.Trigger?.Schedule!, periodDef)
                    ),
                ];
            })
            .addValidation((args) => []);

        return {
            id,
            icon: !days || !period ? <Settings /> : <ChartBar />,
            label: this.createPresetLabel(
                { value: period, placeholder: 'Periodic' },
                { value: days, placeholder: '##', suffix: '-day' },
                { value: costType === 'user-defined' ? undefined : costDesc, placeholder: 'Cost' }
            ),
            description,
            behavior,
        } as INotificationPresetItem<any>;
    }

    private createSchedMonthToDateCostPreset(
        costType: 'usage' | 'total' | 'user-defined',
        options: { recurrence?: RecurrenceRuleType }
    ): INotificationPresetItem<any> {
        const { recurrence } = options;
        const { recurrence: recurrenceDef = recurrence ?? 'Weekly' } = options;
        const costDesc = costType === 'usage' ? 'Usage Cost' : costType === 'total' ? 'Total Cost' : 'Cost';

        const id = this.createId(`${costType}-cost`, recurrence);
        const description = this.createPresetDescription(
            { value: recurrence, placeholder: 'Configurable' },
            'report of',
            costType === 'user-defined' ? 'configured' : costType,
            'cost for the current month'
        );

        type Params = PrefixedParams<Required<typeof options>>;

        const [dateExpr, costExpr] = this.getExprs<[Date, number]>(this.getFields('ChargePeriodStart', 'BilledCost'));

        const initialized = this.createBehavior(({ data }) => {
            const xb = this.createExprBuilder<Params & { pMonth: Date }>();
            const groups = this.selectExprs({ Date: dateExpr });
            const values = this.selectExprs({ Amount: xb.sum(costExpr) });
            const costFilter = costType === 'total' ? [] : [this.createUsageFilterExpr()];

            const result = this.initializeConfig(data, id, {
                Title: `${costDesc} Report`,
                Description: '',
                ComponentConfig: this.createBaseBarChartConfig(groups, values, {
                    xFormat: 'short-date',
                    format: 'money-whole',
                    ...((data.ComponentConfig?.settings ?? {}) as UserConfiguredBarChartSettings),
                    metricLabel: 'Amount',
                    reaggOptions: { sortBy: 'label', sortDir: 'asc' },
                }),
                Trigger: {
                    TriggerType: 'Schedule',
                    Schedule: this.getDefaultScheduleOptions(recurrenceDef),
                },
                QueryOptions: {
                    Parameters: {
                        pMonth: xb.resolve(xb.snapDate(xb.currentDate(), 'month', 'start')),
                    },
                    Filters: [this.createCurrentMonthFilter(), ...costFilter],
                    DatasourceName: this.baseQueryDatasource.name,
                    ApiParameters: { from: xb.resolve(xb.model.pMonth), to: xb.resolve(xb.model.pMonth) },
                },
            });

            return result;
        });
        const behavior = initialized
            .addForm(({ data }) => {
                return [
                    this.createSection(
                        `Report Settings`,
                        this.createSectionItem(`${costDesc} Report Options`, ...this.concatSectionItems(this.createDescriptionInputs(data))),
                        this.createScheduleForm(data.Trigger?.Schedule!, recurrenceDef)
                    ),
                ];
            })
            .addValidation((args) => []);

        return {
            id,
            icon: <ChartBar />,
            label: this.createPresetLabel({ value: recurrence, placeholder: 'Periodic' }, 'month-to-date', { value: costDesc, placeholder: 'Cost' }),
            description,
            behavior,
        } as INotificationPresetItem<any>;
    }

    private createCustomCostReportPreset() {
        const xb = this.createExprBuilder();
        const [category, cost] = this.getExprs<[string, number]>(this.getFields('ServiceCategory', 'BilledCost'));
        const [dimExpr, metricExpr] = this.selectExprs({ category, cost: xb.sum(cost) });
        const monthLookbackInput = this.createMonthLookbackInput();

        const filterInput = this.createFilterSettings();
        const chartInput = this.createCustomChartBehavior(true);

        const behavior = this.createBehavior(({ data }) => {
            return this.mergeConfig(
                {
                    ComponentConfig: this.createCustomChartConfig('pie', dimExpr, metricExpr),
                    QueryOptions: {
                        Parameters: {
                            ...this.createMonthLookbackParams(),
                            ...this.createUserInputParams(data, { pMonths: 1 }),
                        },
                        Filters: [],
                        DatasourceName: this.baseQueryDatasource.name,
                        ApiParameters: this.createApiParams(),
                    },
                    Trigger: this.defaultSchedTrigger('Weekly'),
                },
                data
            );
        })
            .addForm(({ data }) => {
                const chartSettings = chartInput(data);

                const monthsSetting = monthLookbackInput(data);
                const filterSetting = filterInput(data);
                const filterSection = this.createSectionItem('Invoice Line Item Filters', filterSetting, { type: 'divider' }, monthsSetting);

                chartSettings.elements.push(filterSection);

                return [
                    chartSettings,
                    this.createSection(
                        'Report Settings',
                        this.createSectionItem('Title and Description', ...this.createDescriptionInputs(data)),
                        this.createScheduleForm(data.Trigger?.Schedule!)
                    ),
                ];
            })
            .addValidation((args) => []);

        return this.createCustomReportPreset(behavior);
    }

    // #endregion

    protected override getConfiguredSchemaCtx(def: UserDefinedNotificationConfig) {
        const source = (query: Query) => {
            const { ApiParameters, Parameters } = def.QueryOptions ?? {};
            const apiCtx = ApiParameters ? this.exprEvaluator.project(ApiParameters, { parameters: Parameters }) : {};
            return this.invoiceApi.query(query, apiCtx, false);
        };

        return {
            ...this.schemaCtx,
            queryDatasource: {
                ...this.baseQueryDatasource,
                get source() {
                    return source;
                },
            },
        };
    }

    private createCostFieldOptions<T>() {
        const showbackFields = this.fieldCompat.getValidFields('BilledCost', 'AdjustedCashCost', 'AdjustedAmortizedCost');
        if (showbackFields.length > 1) {
            return this.createInput<T>()
                .type('string')
                .options({ label: 'Cost Field', icon: <Coin />, options: showbackFields.map((f) => ({ value: f.path, label: f.name })) })
                .presentation({ width: 150, align: 'center' });
        }
    }

    private createCurrentMonthFilter() {
        const [usageMoField] = this.getFieldExprs('UsageMonth');
        const xb = this.createExprBuilder<{ pMonth: Date }>();
        return xb.resolve(xb.fromExpr(usageMoField).eq(xb.model.pMonth));
    }

    private createDateFilter() {
        const xb = this.createExprBuilder<{ pFrom: Date; pTo: Date }>();
        const [dateField] = this.getFieldExprs('ChargePeriodStart');
        return xb.resolve(xb.and(xb.fromExpr<Date>(dateField).onOrAfter(xb.model.pFrom), xb.fromExpr<Date>(dateField).onOrBefore(xb.model.pTo)));
    }

    private createUsageFilterExpr() {
        const xb = this.createExprBuilder<{}>();

        return xb.resolve(
            xb.or(
                xb.model.ChargeCategory!.eq('Usage'),
                xb.model['lineItem/LineItemType']!.eq([
                    'Usage',
                    'SavingsPlanCoveredUsage',
                    'SavingsPlanNegation',
                    'DiscountedUsage',
                    'Discount',
                    'PrivateRateDiscount',
                    'EdpDiscount',
                ])
            )
        );
    }

    private createExprBuilder<TParams>() {
        type Model = IMixedInvoiceRecord & TParams & { pFrom: Date; pTo: Date };
        const { builder } = exprBuilder<Model>();
        return builder;
    }

    private getFieldExprs(...fields: (keyof IBaseInvoiceRecord)[]) {
        return fields.map((f) => ({ Field: f }));
    }

    private getFields<T extends string[]>(...fields: string[]): T {
        return this.fieldCompat.getAvailableFields(...(fields as (keyof IBaseInvoiceRecord)[])) as T;
    }

    private createApiParams() {
        const xb = this.createExprBuilder<{ pFrom: Date; pTo: Date }>();
        return {
            from: xb.resolve(xb.model.pFrom),
            to: xb.resolve(xb.model.pTo),
        };
    }

    private createMonthLookbackInput() {
        type MonthParam = { pMonths: QueryConstant<number> };
        const input = this.createInput<MonthParam>()
            .type('string')
            .options({
                label: 'Lookback Period',
                options: Array.from({ length: 13 }, (_, i) => ({
                    value: i.toString(),
                    label: i === 0 ? 'Current Month Only' : i === 1 ? '1 Month' : `${i} Months`,
                })),
                defaultValue: '1',
                tooltip: 'Select the number of prior months (including the current month) to include in the report',
                icon: <CalendarStats />,
            })
            .presentation({ align: 'center', width: 160 })
            .map((d) => d.pMonths.Value)
            .modelToView((v) => v.toString())
            .viewToModel((v) => parseInt(v));

        return (data: UserDefinedNotificationConfig, onChanged?: () => void) => input.bind(data.QueryOptions!.Parameters! as MonthParam, onChanged);
    }

    private createMonthLookbackParams() {
        const xb = this.createExprBuilder<{ pFrom: Date; pTo: Date; pMonths: number }>();
        return {
            pFrom: xb.resolve(xb.snapDate(xb.addDate(xb.currentDate(), xb.model.pMonths.times(-1), 'month'), 'month', 'start')),
            pTo: xb.resolve(xb.snapDate(xb.addDate(xb.currentDate(), xb.param(-1), 'day'), 'month', 'start')),
        };
    }
}

@injectable()
export class InvoiceDatasourcePresetsBuilderFactory implements IDatasourcePresetsBuilderFactory {
    public constructor(
        @inject(InvoiceSchemaService) private readonly schemaSvc: InvoiceSchemaService,
        @inject(ExprEvaluator) private readonly exprEvaluator: ExprEvaluator,
        @inject(InvoiceQueryDatasourceFactory) private readonly datasourceFactory: InvoiceQueryDatasourceFactory,
        @inject(InvoiceApiService) private readonly invoiceApi: InvoiceApiService,
        @inject(InvoiceFieldCompatibilityService) private readonly fieldCompat: InvoiceFieldCompatibilityService
    ) {}

    public getSupportedDatasources() {
        return [this.datasourceFactory.getMetadata()];
    }

    public async get(_: string) {
        const [schemaDetail, fieldCompat] = await Promise.all([
            this.schemaSvc.getDailySchemaTransformationDetail(),
            this.fieldCompat.getLookup('daily', { useLegacyFallbacks: true }),
        ]);
        const { types } = schemaDetail;
        const queryDatasource = await this.datasourceFactory.getDatasource((q) => this.invoiceApi.query(q, {}));
        const schemaSvc = SchemaService.create(types);
        return new InvoiceDatasourcePresetsBuilder(queryDatasource, schemaSvc, this.invoiceApi, this.exprEvaluator, fieldCompat);
    }
}
